
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import Dismiss from '@/components/Dismiss.vue';
import { Subscription } from '@/classes/stripe';
import Currency from '@/Models/PrimitiveModels/Currency/Currency';
import { CurrencyCode } from '@/Models/Localization';
import Authenticator from '@/Services/Auth/Authenticator';
import { DiscountInfo } from '@/Services/PromoCodeService';
import PromoCodeService from '@/Services/PromoCodeService';
import {
  trackAcceptDiscountAcceptThisOfferButtonClicked,
  trackRejectDiscountCloseButtonClicked,
  trackRejectDiscountNotThanksButtonClicked,
} from '@/Services/Snowplow/SnowplowService';

@Component({
  components: {
    Dismiss,
  },
})
export default class DiscountOfferModal extends Vue {
  @Prop(Object)
  discountInfo!: DiscountInfo;

  @Prop(Boolean)
  discountInfoLoading!: boolean;

  @Watch('discountInfoLoading')
  onDiscountInfoLoading(val: boolean): void {
    if (!val) {
      this.formatDiscountCodeInformation();
    }
  }

  activeSubscription: Subscription = new Subscription(this.$store.state.subscription.activeSubscription);
  discountedPrice: string = '';
  acceptOfferLoading: boolean = false;

  /**
   * Returns the price of the current subscription (without discount)
   */
  get activeSubscriptionPrice(): string {
    return new Currency(+this.activeSubscription.plan.price).displayPriceMonthly({
      plan: this.activeSubscription.plan,
      selectedInterval: 'month',
      overrideCurrency: this.activeSubscription.plan.currency,
    });
  }

  get currencySymbol(): string {
    const overrideCurrency: string | CurrencyCode = this.activeSubscription.plan.currency;
    return new Currency(+this.activeSubscription.plan.price).displaySymbol({ overrideCurrency });
  }

  get isButtonDisabled(): boolean {
    return this.discountInfoLoading || this.acceptOfferLoading;
  }

  formatDiscountCodeInformation(): void {
    if (this.discountInfo) {
      const discountedPrice = new Currency(
        +this.activeSubscription.plan.price -
          +this.activeSubscription.plan.price * (this.discountInfo.data.coupon.percent_off / 100)
      );

      // Setting the discounted price
      this.discountedPrice = discountedPrice.displayPriceMonthly({
        plan: this.activeSubscription.plan,
        selectedInterval: 'month',
        overrideCurrency: this.activeSubscription.plan.currency,
      });
    }
  }

  created(): void {
    this.formatDiscountCodeInformation();
  }

  async onAcceptOffer(): Promise<void> {
    this.acceptOfferLoading = true;
    const response = await PromoCodeService.assignCodeToTeam({
      coupon_id: this.discountInfo.data.id,
    });

    if (response) {
      trackAcceptDiscountAcceptThisOfferButtonClicked({
        industry_id: this.$store.state?.account?.account?.industry_id ?? null,
        user_id: this.$store.state?.auth?.user?.id,
      });
      await Authenticator.fetchUser();
      await this.$router.push({
        name: 'checkout_complete_with_discount',
        query: {
          discount: `${response.code.coupon.percent_off}`,
        },
      });
    } else {
      this.$emit('closeModal');
    }
    this.acceptOfferLoading = false;
  }

  onDeclineOffer(closeButton = false): void {
    const data = {
      industry_id: this.$store.state?.account?.account?.industry_id ?? null,
      user_id: this.$store.state?.auth?.user?.id,
    };
    if (closeButton) {
      trackRejectDiscountCloseButtonClicked(data);
    } else {
      trackRejectDiscountNotThanksButtonClicked(data);
    }
    this.$emit('declineOffer');
  }
}
